import { useEffect, useRef, useState } from "react";
import { listUnifiedRequestsAPI } from "../../../../utils/api/unifiedRequests";

// Data loading of the Requests sections (In Progress / Completed).
//  * the grouping, the page and the restaurant are sent to the server (nothing
//    is filtered or paged in the browser; no request details are fetched for the list);
//  * the restaurant is the dashboard's selected one - when it changes the old
//    list is dropped at once (never shown for the new restaurant) and the page
//    goes back to 1;
//  * a response only applies if it belongs to the latest query: older in-flight
//    calls are aborted and, belt and braces, ignored by sequence number.

export const PAGE_SIZE = 20;
export const MAX_WINDOW = 1000; // the API's page-mode depth

// Display statuses (see lud-backend/services/requests/statuses.js): the
// terminal ones are "Completed", everything else is still in progress.
export const GROUPS = {
  inProgress: ["pending", "inProgress", "awaitingAction", "awaitingReview", "active", "sent", "failed", "draft", "unknown"],
  // "emailed": a menu e-mail request whose e-mail was sent (final)
  completed: ["approved", "completed", "rejected", "canceled", "emailed"],
};

const aborted = (error) => error?.name === "AbortError";

export function useRequestSection({ group, storeId = "", reloadToken = 0 }) {
  const token = localStorage.getItem("auth-token");
  const [view, setView] = useState({ storeId, page: 1 });
  const page = view.storeId === storeId ? view.page : 1;
  const [list, setList] = useState({ storeId, phase: "loading", data: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    const mine = ++seq.current;
    setList((current) =>
      current.storeId === storeId ? { ...current, phase: current.data ? "refreshing" : "loading", error: null } : { storeId, phase: "loading", data: null, error: null },
    );
    listUnifiedRequestsAPI(token, { status: GROUPS[group].join(","), storeId, page: page > 1 ? page : "", pageSize: PAGE_SIZE }, { signal: controller.signal })
      .then((data) => {
        if (mine !== seq.current) return;
        // the last request of the last page moved away: go back one page
        if (!data.items?.length && page > 1) setView({ storeId, page: page - 1 });
        else setList({ storeId, phase: "ready", data, error: null });
      })
      .catch((error) => {
        if (aborted(error) || mine !== seq.current) return;
        setList((current) => ({ storeId, phase: "error", data: current.storeId === storeId ? current.data : null, error }));
      });
    return () => controller.abort();
  }, [token, group, storeId, page, reloadToken, attempt]);

  const current = list.storeId === storeId ? list : { phase: "loading", data: null, error: null };
  return { phase: current.phase, data: current.data, error: current.error, page, setPage: (next) => setView({ storeId, page: next }), reload: () => setAttempt((value) => value + 1) };
}

